// The destinations per role.
//
//   user app (pa_kpa, ppk, pokja, pejabat_pengadaan, tim_teknis,
//   tim_swakelola, penyedia)
//     One menu in one order for every role, no group titles (Fase 2 of
//     docs/plans/2026-11-02-audit-ui-ux.md): Project (Penyedia only), Generate Dokumen,
//     Review Dokumen, Dashboard PBJ and Alur PBJ (Non-Penyedia only),
//     Regulasi, Dokumen, Data Perusahaan, E-Learning last. Project opens
//     Project Penyedia; the old Penyedia paths (Dashboard, Pantau Paket,
//     Alur Kerja) open it too. A Non-Penyedia reaches Project PBJ
//     (/project-pbj) from Lihat semua in the sidebar Project section. Chat baru,
//     the Project section and Riwayat are the ChatShell's own rows
//     (shell/ChatShell.tsx), not destinations here.
//   admin (admin console, shell/Shell.tsx)
//     Pengguna (Akun, Grup Perusahaan, Persetujuan Pengguna, Fitur per
//     Role), Pengetahuan (Basis Pengetahuan, Sumber Pengetahuan, Update
//     Knowledge, Penyimpanan Pluto, E-Learning), Layanan (Pendampingan,
//     Login Google, Database); the bottom bar keeps the first four and
//     moves the rest and Profil into Lainnya.
//   konsultan (shell/Shell.tsx, POC)
//     Pendampingan (its assigned requests, each with the project
//     read-only) and Regulasi, plus Profil; it signs in at /portal like the admin.
// (docs/contract.md section 9: the admin no longer uses the user-facing app)
import type { Feature, Role } from "../api";
import { isNonPenyedia } from "../lib/labels";
import type { GlyphName } from "../ui/icons";

export interface NavItem {
  id: string;
  /** The full label: sidebar rows and the accessible name everywhere. */
  label: string;
  /** The rail and bottom-bar label. */
  short: string;
  href: string;
  icon: GlyphName;
  /** The role feature that must be visible for the item to show. */
  feature?: Feature;
  /** false keeps the item out of the user app's phone bar (3 to 5 slots); it stays in the sidebar and the drawer. */
  bar?: boolean;
  /** The sidebar group title the item sits under (NAV_GROUP); none for a short menu (konsultan). */
  group?: string;
}

/** Project Penyedia: the paket projects, Temukan Paket and the four stages (Simulasi Pluto v4). */
export const PENYEDIA_PROJECTS_PATH = "/project-penyedia";

/** Old Penyedia menu paths; each now opens Project Penyedia. */
export const PENYEDIA_RETIRED_PATHS = ["/dasbor", "/pantau-paket", "/alur-kerja"];

/** Destinations the bottom bar holds before the rest move to Lainnya. */
export const BAR_SLOTS = 4;

/** Where the admin console lives; every admin route starts with it. */
export const ADMIN_BASE = "/admin";
export const ADMIN_HOME = "/admin/akun";

/** The password sign-in (admin and password accounts); linked from no public page. */
export const PORTAL_PATH = "/portal";
/** The public sign-in: Lanjutkan dengan Google only. */
export const LOGIN_PATH = "/masuk";
/** Onboarding for a new Google account (role "pending"). */
export const ONBOARDING_PATH = "/selamat-datang";

/** Non-Penyedia PBJ pages (migration 0023). */
export const PBJ_DASHBOARD_PATH = "/dashboard-pbj";
export const PBJ_FLOW_PATH = "/alur-pbj";
/** Every PBJ project as a card (reference projects), from the sidebar Project section and the Dashboard PBJ. */
export const PBJ_PROJECTS_PATH = "/project-pbj";

/** The konsultan app (POC): assigned requests and their read-only projects. */
export const KONSULTAN_BASE = "/pendampingan";

/** Pusat Regulasi: every signed-in role of the user app and the konsultan app. */
export const REGULATIONS_PATH = "/regulasi";
const REGULATIONS_ITEM: NavItem = { id: "regulasi", label: "Regulasi", short: "Regulasi", href: REGULATIONS_PATH, icon: "scale", feature: "regulations", bar: false };

/** Data Perusahaan: company documents and the company group, every user-app role (optional). */
export const DATA_PERUSAHAAN_PATH = "/data-perusahaan";
const DATA_PERUSAHAAN_ITEM: NavItem = { id: "data-perusahaan", label: "Data Perusahaan", short: "Perusahaan", href: DATA_PERUSAHAAN_PATH, icon: "building", feature: "company_data", bar: false };

/** Review Dokumen: Pluto reviews an uploaded or pasted document, every user-app role (migration 0044). */
export const REVIEW_DOKUMEN_PATH = "/review-dokumen";
const REVIEW_DOKUMEN_ITEM: NavItem = { id: "review-dokumen", label: "Review Dokumen", short: "Review", href: REVIEW_DOKUMEN_PATH, icon: "shieldTick", feature: "document_review", bar: false };

export function isKonsultanPath(path: string): boolean {
  return path === KONSULTAN_BASE || path.startsWith(`${KONSULTAN_BASE}/`);
}

export const PROFILE_ITEM: NavItem = { id: "profil", label: "Profil", short: "Profil", href: "/profil", icon: "profileCircle" };
export const ADMIN_PROFILE_ITEM: NavItem = { ...PROFILE_ITEM, href: "/admin/profil" };

export function isAdminPath(path: string): boolean {
  return path === ADMIN_BASE || path.startsWith(`${ADMIN_BASE}/`);
}

/** The brand link target: the console for an admin, Pendampingan for a konsultan, the chat for everyone else. */
export function homeFor(role: Role): string {
  if (role === "konsultan") return KONSULTAN_BASE;
  return role === "admin" ? ADMIN_HOME : "/";
}

export function profileItemFor(role: Role): NavItem {
  return role === "admin" ? ADMIN_PROFILE_ITEM : PROFILE_ITEM;
}

/** The admin sidebar group titles (docs/design/glossary.md); the user app's menu has none. */
export const NAV_GROUP = {
  pengguna: "Pengguna",
  pengetahuan: "Pengetahuan",
  layanan: "Layanan",
} as const;

/**
 * One menu for every user-app role, in one order and without group titles
 * (Fase 2): Project (a Penyedia only), Generate Dokumen, Review Dokumen, Dashboard PBJ and
 * Alur PBJ (a Non-Penyedia only), Regulasi, Dokumen, Data Perusahaan, then E-Learning
 * last (meeting of 28 Oct 2026). A menu a role does not have is left out;
 * the others keep their order. The phone bar holds the first three of
 * Project, Generate Dokumen, Regulasi, Dokumen and E-Learning the role has.
 * Only the admin console groups its menu (Pengguna, Pengetahuan, Layanan).
 */
export function navFor(role: Role): NavItem[] {
  if (role === "admin") {
    const { pengguna, pengetahuan, layanan } = NAV_GROUP;
    return [
      { id: "akun", label: "Akun", short: "Akun", href: ADMIN_HOME, icon: "profile2user", group: pengguna },
      { id: "grupperusahaan", label: "Grup Perusahaan", short: "Grup", href: "/admin/grup-perusahaan", icon: "building", group: pengguna },
      { id: "persetujuan", label: "Persetujuan Pengguna", short: "Persetujuan", href: "/admin/persetujuan", icon: "taskSquare", group: pengguna },
      { id: "fitur", label: "Fitur per Role", short: "Fitur", href: "/admin/fitur-role", icon: "shieldTick", group: pengguna },
      { id: "pengetahuan", label: "Basis Pengetahuan", short: "Pengetahuan", href: "/admin/pengetahuan", icon: "bookOpen", group: pengetahuan },
      { id: "sumber", label: "Sumber Pengetahuan", short: "Sumber", href: "/admin/sumber-pengetahuan", icon: "link", group: pengetahuan },
      { id: "updateknowledge", label: "Update Knowledge", short: "Update", href: "/admin/update-knowledge", icon: "refresh", group: pengetahuan },
      { id: "penyimpanan", label: "Penyimpanan Pluto", short: "Penyimpanan", href: "/admin/penyimpanan-pluto", icon: "folder", group: pengetahuan },
      { id: "elearning", label: "E-Learning", short: "Belajar", href: "/admin/elearning", icon: "book", group: pengetahuan },
      { id: "pendampingan", label: "Pendampingan", short: "Pendampingan", href: "/admin/pendampingan", icon: "messages", group: layanan },
      { id: "logingoogle", label: "Login Google", short: "Google", href: "/admin/login-google", icon: "lock", group: layanan },
      { id: "basisdata", label: "Database", short: "Database", href: "/admin/basis-data", icon: "layers", group: layanan },
    ];
  }
  if (role === "konsultan") {
    return [{ id: "pendampingan", label: "Pendampingan", short: "Pendampingan", href: KONSULTAN_BASE, icon: "messages" }, REGULATIONS_ITEM];
  }
  if (role === "pending") return [];
  const nonPenyedia = isNonPenyedia(role);
  const items: (NavItem | null)[] = [
    // A Non-Penyedia has no Project menu: its projects are in the sidebar Project section (Lihat semua opens /project-pbj).
    role === "penyedia" ? { id: "project-penyedia", label: "Project", short: "Project", href: PENYEDIA_PROJECTS_PATH, icon: "folder", feature: "projects" } : null,
    { id: "template", label: "Generate Dokumen", short: "Generate", href: "/template", icon: "docAdd", feature: "templates" },
    REVIEW_DOKUMEN_ITEM,
    nonPenyedia ? { id: "dashboard-pbj", label: "Dashboard PBJ", short: "Dashboard", href: PBJ_DASHBOARD_PATH, icon: "element3", feature: "pbj_dashboard", bar: false } : null,
    nonPenyedia ? { id: "alur-pbj", label: "Alur PBJ", short: "Alur PBJ", href: PBJ_FLOW_PATH, icon: "layers", feature: "pbj_flow", bar: false } : null,
    { ...REGULATIONS_ITEM, bar: true },
    { id: "pengetahuan", label: "Dokumen", short: "Dokumen", href: "/pengetahuan", icon: "bookOpen", feature: "library" },
    DATA_PERUSAHAAN_ITEM,
    { id: "belajar", label: "E-Learning", short: "Belajar", href: "/belajar", icon: "book", feature: "elearning" },
  ];
  return items.filter((d): d is NavItem => d !== null);
}

/** Items in their order, cut into runs of one group (an item without a group is a run of its own kind). */
export function groupNav(items: NavItem[]): { title: string | null; items: NavItem[] }[] {
  const out: { title: string | null; items: NavItem[] }[] = [];
  for (const item of items) {
    const title = item.group ?? null;
    const last = out[out.length - 1];
    if (last && last.title === title) last.items.push(item);
    else out.push({ title, items: [item] });
  }
  return out;
}

/**
 * The menu a guest sees in the ChatShell: the destinations every user-app role
 * has (Generate Dokumen, Regulasi, Dokumen, E-Learning), so nothing a guest saw goes
 * missing after signing in. Each row asks the guest to sign in first
 * (shell/SignInGate.tsx) and returns to the page through ?next=.
 */
export function guestNav(): NavItem[] {
  const keep = ["template", "regulasi", "pengetahuan", "belajar"];
  return navFor("pa_kpa").filter((d) => keep.includes(d.id));
}

/**
 * The bottom bar split for the admin console: what sits in the bar, and what
 * Lainnya opens. When every destination and Profil fit the bar, Profil sits
 * in the bar and Lainnya is not shown (`more` is empty).
 */
export function barSplit(role: Role): { bar: NavItem[]; more: NavItem[] } {
  const all = navFor(role);
  const profile = profileItemFor(role);
  if (all.length + 1 <= BAR_SLOTS) return { bar: [...all, profile], more: [] };
  return { bar: all.slice(0, BAR_SLOTS), more: [...all.slice(BAR_SLOTS), profile] };
}
